import cloneDeep from 'lodash/fp/cloneDeep';
import React, {createContext, useContext, useState} from 'react';

import {BackgroundColor, SavedData, saveObject} from '#Common/savedData';

type SavedDataContextType = {
  savedData: SavedData;
  isUserNew: boolean;
  updateBackgroundColor: (backgroundColor: BackgroundColor) => void;
  updateUserId: (userId: string) => void;
  updatePuzzleData: (
    puzzleId: string,
    puzzleDate: string,
    grid: Record<string, string>,
  ) => void;
  updateSavedDifficulty: (difficulty: number) => void;
  updateSavedUserName: (userName: string) => void;
  updateViewedNewsIndex: (index: number) => void;
};

const SavedDataContext = createContext<SavedDataContextType | null>(null);

export const SavedDataProvider = ({
  data,
  isUserNew,
  children,
}: {
  data: SavedData;
  isUserNew: boolean;
  children: React.ReactNode;
}) => {
  const [savedData, setSavedData] = useState<SavedData>(data);
  const update = async (data: SavedData) => {
    setSavedData(data);
    await saveObject(data);
  };

  const updateField =
    <K extends keyof SavedData>(field: K) =>
    async (input: SavedData[K]) => {
      if (!input) {
        return;
      }
      const savedDataCopy = cloneDeep(savedData);
      if (savedData[field] === input) {
        return;
      }
      savedDataCopy[field] = input;
      await update(savedDataCopy);
    };

  const updateBackgroundColor = updateField('backgroundColor');
  const updateUserId = updateField('userId');
  const updateSavedUserName = updateField('userName');
  const updateSavedDifficulty = updateField('difficulty');
  const updateViewedNewsIndex = updateField('viewedNewsIndex');

  const updatePuzzleData = (
    puzzleId: string,
    puzzleDate: string,
    grid: Record<string, string>,
  ): void => {
    if (!puzzleId) {
      return;
    }
    const savedDataCopy = cloneDeep(savedData);
    savedDataCopy.data = savedDataCopy.data || {
      dateString: '',
      puzzleStates: {},
    };
    // if dates differ, reset puzzleStates;
    if (savedDataCopy.data.dateString !== puzzleDate) {
      savedDataCopy.data.puzzleStates = {};
    }
    savedDataCopy.data.dateString = puzzleDate;
    savedDataCopy.data.puzzleStates[puzzleId] = grid;
    update(savedDataCopy);
  };

  return (
    <SavedDataContext.Provider
      value={{
        savedData,
        isUserNew,
        updateBackgroundColor,
        updateUserId,
        updatePuzzleData,
        updateSavedDifficulty,
        updateSavedUserName,
        updateViewedNewsIndex,
      }}
    >
      {children}
    </SavedDataContext.Provider>
  );
};

export const useSavedData = (): SavedDataContextType => {
  const savedData = useContext(SavedDataContext);
  if (!savedData) {
    throw new Error('user data unavailable');
  }
  return savedData;
};
